import { Component, Inject, OnChanges, SimpleChanges } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ProviderSelectionDetail, RideRequestDetail, RideRequestService } from '@uzurv/clara';
import { isNullOrUndefined } from '@uzurv/core';
import { AlertDialogComponent } from '../alert-dialog/alert-dialog.component';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';

export interface SelectProviderDialogData {
  rideRequest: RideRequestDetail;
}

@Component({
  selector: 'app-select-provider-dialog',
  templateUrl: './select-provider-dialog.component.html',
  styleUrls: ['./select-provider-dialog.component.scss']
})
export class SelectProviderDialogComponent implements OnChanges {
  isShowingOtherProviders: boolean = false;

  private _interestedProviders: Array<ProviderSelectionDetail> = [];
  private _providersProposingNewTime: Array<ProviderSelectionDetail> = [];
  private _providersHaveNotExpressedInterest: Array<ProviderSelectionDetail> = [];
  private _providersIgnoringRideRequest: Array<ProviderSelectionDetail> = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: SelectProviderDialogData,
    private dialog: MatDialog,
    private dialogRef: MatDialogRef<SelectProviderDialogComponent>,
    private rideRequestService: RideRequestService
  ) {
    this.populateDialog();
  }

  /**
   * Repopulate dialog if data changes.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data']) {
      this.populateDialog();
    }
  }

  /**
   * Returns array of providers who have expressed interest in the ride request.
   */
  get interestedProviders(): Array<ProviderSelectionDetail> {
    return this._interestedProviders;
  }

  /**
   * Returns array of providers who have proposed a new time for the ride request.
   */
  get providersProposingNewTime(): Array<ProviderSelectionDetail> {
    return this._providersProposingNewTime;
  }

  /**
   * Returns array of peroviders who have neither expressed interest nor ignored the ride request.
   */
  get providersHaveNotExpressedInterest(): Array<ProviderSelectionDetail> {
    return this._providersHaveNotExpressedInterest;
  }

  /**
   * Returns array of providers who have ignored the ride request.
   */
  get providersIgnoringRideRequest(): Array<ProviderSelectionDetail> {
    return this._providersIgnoringRideRequest;
  }

  /**
   * Populates the providers dialog with data.
   */
  populateDialog() {
    if (!isNullOrUndefined(this.data.rideRequest)) {
      this.fetchProviders();
    }
  }

  /**
   * Returns an array of available providers for the ride request.
   */
  fetchProviders() {
    if (isNullOrUndefined(this.data?.rideRequest)) {
      return;
    }

    this.rideRequestService.listPairableProvidersByRideRequestId(this.data.rideRequest.id).subscribe(
      pairableProviders => {
        if (!isNullOrUndefined(pairableProviders) && pairableProviders.length > 0) {
          pairableProviders.forEach(provider => {
            if (!isNullOrUndefined(provider.interestedDriver) && !provider.providerIgnoringRide) {
              if (provider.interestedDriver.proposingNewPickupTime) {
                this._providersProposingNewTime.push(provider);
              } else {
                this._interestedProviders.push(provider);
              }
            } else if (provider.providerIgnoringRide) {
              this._providersIgnoringRideRequest.push(provider);
            } else {
              this.providersHaveNotExpressedInterest.push(provider);
            }
          });

          // Sort providers proposing a new time by time (earliest to latest).
          // If times are the same, sort by provider name.
          if (this._providersProposingNewTime.length > 0) {
            this._providersProposingNewTime.sort(
              (a, b) =>
                a.interestedDriver.proposedPickupTime.getTime() - b.interestedDriver.proposedPickupTime.getTime() ||
                a.provider.name.localeCompare(b.provider.name)
            );
          }

          // Alphabetize providers who have ignored the ride.
          if (this._providersIgnoringRideRequest.length > 0) {
            this._providersIgnoringRideRequest.sort((a, b) => a.provider.name.localeCompare(b.provider.name));
          }
        }
      },
      err => {
        this.dialog.open(AlertDialogComponent, {
          data: {
            title: 'Error',
            message: err.message
          }
        });
      }
    );
  }

  /**
   * Get the toggle button text based on the current state of the toggle.
   */
  getOtherProvidersButtonText(): string {
    if (this.isShowingOtherProviders) {
      return '- Hide Other Providers';
    } else {
      return '+ Show Other Providers';
    }
  }

  /**
   * Toggle hiding and showing additional providers.
   */
  toggleOtherProviders() {
    this.isShowingOtherProviders = !this.isShowingOtherProviders;
  }

  /**
   * Handles showing the confirmation dialog when a provider is selected.
   * Determines the messaging based on whether the provider has expressed interest in general, not expressed interest at all, or ignored the ride.
   *
   * @param providerSelectionDetail
   * @param isUninterestedProvider
   * @param isIgnoredProvider
   */
  handleProviderSelected(providerSelectionDetail: ProviderSelectionDetail) {
    let dialogMessage = '';
    let svgIcon;
    let svgClass;

    if (providerSelectionDetail.interestedDriver && !providerSelectionDetail.providerIgnoringRide) {
      dialogMessage = `Are you sure you want to select ${providerSelectionDetail.interestedDriver.driver.firstName} ${
        providerSelectionDetail.interestedDriver.driver.lastName ? providerSelectionDetail.interestedDriver.driver.lastName : ''
      } for this reservation?`;
    } else if (isNullOrUndefined(providerSelectionDetail.interestedDriver) && !providerSelectionDetail.providerIgnoringRide) {
      dialogMessage = `${providerSelectionDetail.provider.name} did not express interest in this ride using web self service. Has the provider confirmed they will take this ride?`;
      svgIcon = 'uz-warning';
      svgClass = 'warning';
    } else if (providerSelectionDetail.providerIgnoringRide) {
      dialogMessage = `${providerSelectionDetail.provider.name} ignored this ride using web self service. Has the provider confirmed they will now take this ride?`;
      svgIcon = 'uz-warning';
      svgClass = 'warning';
    }

    this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          svgIcon: svgIcon,
          svgClass: svgClass,
          title: 'Are You Sure?',
          message: dialogMessage,
          isDestructive: false
        }
      })
      .afterClosed()
      .subscribe(confirmed => {
        if (confirmed) {
          this.dialogRef.close({
            providerSelectionDetail: providerSelectionDetail
          });
        }
      });
  }

  /**
   * Close the select modal.
   */
  close(): void {
    this.dialogRef.close();
  }
}
